import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { CountryListInput, CountrySelect, CurrencyListInput, Select } from '@/components/form';
import type { ClientDetail } from './types';
import type { UpdateClientPayload } from './useClientDetail';
import { EmailInput, PhoneInput, UrlInput, isPhoneValid } from './inputs';
import { useLookup } from '@/hooks/useLookups';
import { validateEmail, validateUrl } from './validation';

// ---------------------------------------------------------------------------
// Form shape
// ---------------------------------------------------------------------------

interface EditClientForm {
  // Section 1: Company Details
  companyName: string;
  legalFormId: string;
  countryCode: string;
  incorporationDate: string;
  incorporationNumber: string;
  tin: string;
  contactEmail: string;
  telephone: string;
  registeredAddressLine1: string;
  registeredAddressLine2: string;
  registeredAddressCity: string;
  registeredAddressState: string;
  registeredAddressZip: string;
  registeredAddressCountryCode: string;
  businessAddressLine1: string;
  businessAddressLine2: string;
  businessAddressCity: string;
  businessAddressState: string;
  businessAddressZip: string;
  businessAddressCountryCode: string;
  // Section 2: Account Usage & Compliance
  businessSectorId: string;
  website: string;
  activityDescription: string;
  purposeOfAccount: string;
  intendedUse: string;
  sourceOfIncome: string;
  monthlyIncome: string;
  estMonthlyIncomingTxns: string;
  estMonthlyIncomingVolume: string;
  estMonthlyOutgoingTxns: string;
  estMonthlyOutgoingVolume: string;
  plannedCountriesIn: string[];
  plannedCountriesOut: string[];
  plannedCurrencies: string[];
}

type FormErrors = Partial<Record<keyof EditClientForm, string>>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EditClientModalProps {
  isOpen: boolean;
  client: ClientDetail;
  onClose: () => void;
  onSave: (payload: UpdateClientPayload) => Promise<void>;
  /**
   * Optional belt-and-braces refetch hook. updateClient already calls
   * setClient with the PUT response, but the webapp normalises some
   * values server-side (trim, clamp). Calling this after a successful
   * save guarantees the page shows the canonical row.
   */
  onRefresh?: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clientToForm(client: ClientDetail): EditClientForm {
  return {
    companyName: client.company_name ?? '',
    legalFormId: client.legal_form_id != null ? String(client.legal_form_id) : '',
    countryCode: client.country_code ?? '',
    incorporationDate: client.incorporation_date ?? '',
    incorporationNumber: client.incorporation_number ?? '',
    tin: client.tin ?? '',
    contactEmail: client.contact_email ?? '',
    telephone: client.telephone ?? '',
    registeredAddressLine1: client.registered_address_line1 ?? '',
    registeredAddressLine2: client.registered_address_line2 ?? '',
    registeredAddressCity: client.registered_address_city ?? '',
    registeredAddressState: client.registered_address_state ?? '',
    registeredAddressZip: client.registered_address_zip ?? '',
    registeredAddressCountryCode: client.registered_address_country_code ?? '',
    businessAddressLine1: client.business_address_line1 ?? '',
    businessAddressLine2: client.business_address_line2 ?? '',
    businessAddressCity: client.business_address_city ?? '',
    businessAddressState: client.business_address_state ?? '',
    businessAddressZip: client.business_address_zip ?? '',
    businessAddressCountryCode: client.business_address_country_code ?? '',
    businessSectorId: client.business_sector_id != null ? String(client.business_sector_id) : '',
    website: client.website ?? '',
    activityDescription: client.activity_description ?? '',
    purposeOfAccount: client.purpose_of_account ?? '',
    intendedUse: client.intended_use ?? '',
    sourceOfIncome: client.source_of_income ?? '',
    monthlyIncome: client.monthly_income?.toString() ?? '',
    estMonthlyIncomingTxns:
      client.estimated_incoming_tx_count != null ? String(client.estimated_incoming_tx_count) : '',
    estMonthlyIncomingVolume:
      client.estimated_incoming_volume != null ? String(client.estimated_incoming_volume) : '',
    estMonthlyOutgoingTxns:
      client.estimated_outgoing_tx_count != null ? String(client.estimated_outgoing_tx_count) : '',
    estMonthlyOutgoingVolume:
      client.estimated_outgoing_volume != null ? String(client.estimated_outgoing_volume) : '',
    plannedCountriesIn: client.planned_countries_in ?? [],
    plannedCountriesOut: client.planned_countries_out ?? [],
    plannedCurrencies: client.planned_currencies ?? [],
  };
}

/**
 * Convert a form string to a payload value.
 * Non-empty → trimmed string; empty → null (tells backend to clear the field).
 */
function strOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

function numOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : null;
}

/**
 * Deux listes de codes sont la meme saisie, ou non.
 *
 * Le diff compare les autres champs par `===`, ce qu'un tableau ne supporte
 * pas : retirer puis remettre un pays rend un tableau neuf, et un champ non
 * touche partirait a chaque enregistrement.
 */
function sameCodes(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((code, i) => code === b[i]);
}

/**
 * Field dispatch table: each EditClientForm key maps to its payload key
 * and the coercion used when the value lands on the wire.
 *
 * Two coercions matter today:
 *   - 'str' → strOrNull (empty string clears the column server-side)
 *   - 'num' → numOrNull
 *
 * The companyName is a special case: it's the only required field, and we
 * always trim it (not strOrNull, which would emit null and be rejected by
 * the validate() guard).
 */
type FieldKind = 'str' | 'num' | 'codes' | 'companyName';
const FIELD_MAP: Array<[keyof EditClientForm, keyof UpdateClientPayload, FieldKind]> = [
  ['companyName', 'companyName', 'companyName'],
  ['legalFormId', 'legalFormId', 'num'],
  ['countryCode', 'countryCode', 'str'],
  ['incorporationDate', 'incorporationDate', 'str'],
  ['incorporationNumber', 'incorporationNumber', 'str'],
  ['tin', 'tin', 'str'],
  ['contactEmail', 'contactEmail', 'str'],
  ['telephone', 'telephone', 'str'],
  ['registeredAddressLine1', 'registeredAddressLine1', 'str'],
  ['registeredAddressLine2', 'registeredAddressLine2', 'str'],
  ['registeredAddressCity', 'registeredAddressCity', 'str'],
  ['registeredAddressState', 'registeredAddressState', 'str'],
  ['registeredAddressZip', 'registeredAddressZip', 'str'],
  ['registeredAddressCountryCode', 'registeredAddressCountryCode', 'str'],
  ['businessAddressLine1', 'businessAddressLine1', 'str'],
  ['businessAddressLine2', 'businessAddressLine2', 'str'],
  ['businessAddressCity', 'businessAddressCity', 'str'],
  ['businessAddressState', 'businessAddressState', 'str'],
  ['businessAddressZip', 'businessAddressZip', 'str'],
  ['businessAddressCountryCode', 'businessAddressCountryCode', 'str'],
  ['businessSectorId', 'businessSectorId', 'num'],
  ['website', 'website', 'str'],

  ['activityDescription', 'activityDescription', 'str'],
  ['purposeOfAccount', 'purposeOfAccount', 'str'],
  ['intendedUse', 'intendedUse', 'str'],
  ['sourceOfIncome', 'sourceOfIncome', 'str'],
  ['monthlyIncome', 'monthlyIncome', 'num'],
  ['estMonthlyIncomingTxns', 'estimatedIncomingTxCount', 'num'],
  ['estMonthlyIncomingVolume', 'estimatedIncomingVolume', 'num'],
  ['estMonthlyOutgoingTxns', 'estimatedOutgoingTxCount', 'num'],
  ['estMonthlyOutgoingVolume', 'estimatedOutgoingVolume', 'num'],
  ['plannedCountriesIn', 'plannedCountriesIn', 'codes'],
  ['plannedCountriesOut', 'plannedCountriesOut', 'codes'],
  ['plannedCurrencies', 'plannedCurrencies', 'codes'],
];

/**
 * Les deux adresses, champ par champ (#258).
 *
 * L'adresse operationnelle est la meme que l'adresse enregistree pour la
 * plupart des clients, et l'operateur la saisissait deux fois.
 */
const ADDRESS_MIRROR = [
  ['businessAddressLine1', 'registeredAddressLine1'],
  ['businessAddressLine2', 'registeredAddressLine2'],
  ['businessAddressCity', 'registeredAddressCity'],
  ['businessAddressState', 'registeredAddressState'],
  ['businessAddressZip', 'registeredAddressZip'],
  ['businessAddressCountryCode', 'registeredAddressCountryCode'],
] as const satisfies ReadonlyArray<readonly [keyof EditClientForm, keyof EditClientForm]>;

/** L'adresse operationnelle telle qu'elle est, pour pouvoir la rendre. */
function snapshotBusinessAddress(form: EditClientForm): Partial<EditClientForm> {
  return Object.fromEntries(
    ADDRESS_MIRROR.map(([business]) => [business, form[business]]),
  ) as Partial<EditClientForm>;
}

/** L'adresse operationnelle alignee sur l'adresse enregistree. */
function mirrorRegisteredAddress(form: EditClientForm): EditClientForm {
  const next = { ...form } as Record<string, unknown>;
  for (const [business, registered] of ADDRESS_MIRROR) {
    next[business] = form[registered];
  }
  return next as unknown as EditClientForm;
}

/**
 * La case s'ouvre deja cochee quand les deux adresses coincident deja — mais
 * pas sur une fiche vide, ou « identiques » ne voudrait rien dire et ou cocher
 * desactiverait des champs que personne n'a remplis.
 */
function addressesAlreadyMatch(form: EditClientForm): boolean {
  if (!form.registeredAddressLine1.trim()) return false;
  return ADDRESS_MIRROR.every(([business, registered]) => form[business] === form[registered]);
}

/**
 * Diff the current form against the snapshot taken when the modal opened.
 * Returns a partial payload containing ONLY the keys whose form value
 * differs from the initial one. This keeps the wire payload small and
 * gives us partial-update semantics (touching only Telephone won't
 * silently re-send + clobber every other field) without needing optimistic
 * locking — see B17 in the master plan for the design rationale.
 */
export function diffPayload(current: EditClientForm, initial: EditClientForm): UpdateClientPayload {
  const out: UpdateClientPayload = {};
  for (const [formKey, payloadKey, kind] of FIELD_MAP) {
    if (kind === 'codes') {
      const next = current[formKey] as string[];
      const before = initial[formKey] as string[];
      if (sameCodes(next, before)) continue;
      (out as Record<string, unknown>)[payloadKey] = next;
      continue;
    }
    if (current[formKey] === initial[formKey]) continue;
    if (kind === 'companyName') {
      out.companyName = current.companyName.trim();
    } else if (kind === 'num') {
      (out as Record<string, unknown>)[payloadKey] = numOrNull(current[formKey] as string);
    } else {
      (out as Record<string, unknown>)[payloadKey] = strOrNull(current[formKey] as string);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EditClientModal({
  isOpen,
  client,
  onClose,
  onSave,
  onRefresh,
}: EditClientModalProps) {
  const [initialForm, setInitialForm] = useState<EditClientForm>(() => clientToForm(client));
  const [form, setForm] = useState<EditClientForm>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [sameAsRegistered, setSameAsRegistered] = useState(() =>
    addressesAlreadyMatch(initialForm),
  );
  // Ce que l'adresse operationnelle portait avant qu'on coche, pour qu'un clic
  // de travers ne coute rien.
  const businessAddressBackup = useRef<Partial<EditClientForm> | null>(null);

  const { items: legalForms, loading: legalFormsLoading } = useLookup('legal-forms');
  const { items: businessSectors, loading: businessSectorsLoading } = useLookup('business-sectors');

  const legalFormOptions = legalForms.map((it) => ({ value: String(it.id), label: it.label }));
  const businessSectorOptions = businessSectors.map((it) => ({
    value: String(it.id),
    label: it.label,
  }));

  // Reset form (and the diff baseline) when the client changes or modal opens.
  useEffect(() => {
    if (isOpen) {
      const fresh = clientToForm(client);
      setInitialForm(fresh);
      setForm(fresh);
      setErrors({});
      const linked = addressesAlreadyMatch(fresh);
      setSameAsRegistered(linked);
      // Ouvrir deja coche garde quand meme la copie : sans elle, modifier
      // l'adresse enregistree puis decocher laisserait l'adresse
      // operationnelle sur la valeur recopiee, et un enregistrement
      // l'ecraserait.
      businessAddressBackup.current = linked ? snapshotBusinessAddress(fresh) : null;
    }
  }, [isOpen, client]);

  // Memoise the partial payload so the dirty-count rendering and the
  // submit path agree on exactly what's about to be sent.
  const partialPayload = useMemo(() => diffPayload(form, initialForm), [form, initialForm]);
  const dirtyKeyCount = Object.keys(partialPayload).length;
  const isDirty = dirtyKeyCount > 0;

  const updateField = useCallback(
    (field: keyof EditClientForm, value: string | string[]) => {
      setForm((prev) => {
        const next = { ...prev, [field]: value } as Record<string, unknown>;
        // Tant que la case est cochee, l'adresse operationnelle suit celle
        // qu'on est en train de taper, sans quoi elle garderait la precedente.
        if (sameAsRegistered) {
          const twin = ADDRESS_MIRROR.find(([, registered]) => registered === field);
          if (twin) next[twin[0]] = value;
        }
        return next as unknown as EditClientForm;
      });
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors, sameAsRegistered],
  );

  const toggleSameAddress = useCallback(
    (checked: boolean) => {
      setSameAsRegistered(checked);
      // La copie se prend et se rend ici, hors de l'updater : React peut
      // rappeler celui-ci, et un second passage trouverait la copie deja
      // consommee puis rendrait le formulaire tel quel — l'adresse resterait
      // recopiee alors que la case est decochee.
      if (checked) {
        businessAddressBackup.current = snapshotBusinessAddress(form);
        setForm(mirrorRegisteredAddress);
        return;
      }
      const backup = businessAddressBackup.current;
      businessAddressBackup.current = null;
      setForm((prev) => (backup ? { ...prev, ...backup } : prev));
    },
    [form],
  );

  const validate = useCallback((): boolean => {
    const errs: FormErrors = {};
    // companyName is only validated when the operator has actually
    // changed it. The original record always has a non-empty company
    // name; the only way this guard kicks in is if the operator clears
    // the field, which we still want to block.
    if ('companyName' in partialPayload && !form.companyName.trim()) {
      errs.companyName = 'Company name is required';
    }
    if (form.contactEmail.trim() && !validateEmail(form.contactEmail)) {
      errs.contactEmail = 'Invalid email format';
    }
    if (form.website.trim() && !validateUrl(form.website)) {
      errs.website = 'URL must start with http:// or https://';
    }
    if (!isPhoneValid(form.telephone)) {
      errs.telephone = 'Invalid phone number — include the country code (e.g. +33…)';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form, partialPayload]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;
    if (!isDirty) {
      // Belt-and-braces: the Save button is already disabled when no
      // fields have changed, but defending the call site keeps a stray
      // submit (keyboard, hot-reload) from issuing an empty PUT.
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      await onSave(partialPayload);
      // Refetch the client so the page reflects the canonical server
      // representation (server-side trims / clamps / normalisations).
      // Best-effort — a failure here is non-blocking; we still close
      // the modal because the save itself succeeded.
      try {
        await onRefresh?.();
      } catch {
        // ignore
      }
      onClose();
    } catch {
      // Error toast already displayed by hook — keep modal open
    } finally {
      setSubmitting(false);
    }
  }, [validate, isDirty, partialPayload, onSave, onRefresh, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      title="Edit Client"
      onClose={handleCancel}
      className="max-w-[720px]"
      footer={
        <>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={submitting || !isDirty}>
            {submitting
              ? 'Saving...'
              : isDirty
                ? `Save Changes (${dirtyKeyCount} field${dirtyKeyCount === 1 ? '' : 's'})`
                : 'Save Changes'}
          </Button>
        </>
      }
    >
      <div className="max-h-[65vh] overflow-y-auto space-y-4">
        {/* ── Section 1: Company Details ──────────────────────────────── */}
        <div className="text-[13px] font-bold text-[#1a1a1a] uppercase tracking-[0.5px]">
          1. Company Details
        </div>

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">Company Name *</label>
          <Input
            value={form.companyName}
            onChange={(v) => updateField('companyName', v)}
            error={errors.companyName}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">Legal Form</label>
            <Select
              value={form.legalFormId}
              onChange={(v) => updateField('legalFormId', v)}
              options={legalFormOptions}
              placeholder={legalFormsLoading ? 'Loading…' : 'Select legal form…'}
              disabled={legalFormsLoading}
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Country of Incorporation
            </label>
            <CountrySelect
              value={form.countryCode}
              onChange={(v) => updateField('countryCode', v)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Incorporation Date
            </label>
            <input
              type="date"
              value={form.incorporationDate}
              onChange={(e) => updateField('incorporationDate', e.target.value)}
              className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Incorporation Number
            </label>
            <Input
              value={form.incorporationNumber}
              onChange={(v) => updateField('incorporationNumber', v)}
              placeholder="e.g. B-224578"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">TIN</label>
            <Input
              value={form.tin}
              onChange={(v) => updateField('tin', v)}
              placeholder="e.g. LU12345678"
              // `client_business.tin` est en varchar(20). Le DTO du CBS en
              // accepte 50, donc une valeur plus longue passe la validation et
              // se fait refuser par Postgres : on s'arrete a la colonne, pas au
              // DTO, pour ne pas laisser saisir ce qui ne s'enregistrera pas.
              maxLength={20}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Contact Email
            </label>
            <EmailInput
              value={form.contactEmail}
              onChange={(v) => updateField('contactEmail', v)}
              error={errors.contactEmail}
              placeholder="e.g. contact@company.com"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">Telephone</label>
            <PhoneInput
              value={form.telephone}
              onChange={(v) => updateField('telephone', v)}
              error={errors.telephone}
              placeholder="e.g. +352 123 456"
            />
          </div>
        </div>

        <div className="text-[10px] font-semibold text-[#888] uppercase tracking-[1px] mt-2">
          Registered Address
        </div>
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">
            Street Address (Line 1)
          </label>
          <Input
            value={form.registeredAddressLine1}
            onChange={(v) => updateField('registeredAddressLine1', v)}
            placeholder="e.g. 12 Boulevard Royal"
          />
        </div>
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">
            Street Address (Line 2)
          </label>
          <Input
            value={form.registeredAddressLine2}
            onChange={(v) => updateField('registeredAddressLine2', v)}
            placeholder="Apartment, suite, building"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">City</label>
            <Input
              value={form.registeredAddressCity}
              onChange={(v) => updateField('registeredAddressCity', v)}
              placeholder="e.g. Luxembourg City"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              State / Region
            </label>
            <Input
              value={form.registeredAddressState}
              onChange={(v) => updateField('registeredAddressState', v)}
              placeholder="State or region"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Zip / Postal Code
            </label>
            <Input
              value={form.registeredAddressZip}
              onChange={(v) => updateField('registeredAddressZip', v)}
              placeholder="e.g. L-2449"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">Country</label>
            <CountrySelect
              value={form.registeredAddressCountryCode}
              onChange={(v) => updateField('registeredAddressCountryCode', v)}
            />
          </div>
        </div>

        <div className="text-[10px] font-semibold text-[#888] uppercase tracking-[1px] mt-2">
          Business Address
        </div>
        <label className="flex items-center gap-2 text-[12px] text-[#666]">
          <input
            type="checkbox"
            checked={sameAsRegistered}
            onChange={(e) => toggleSameAddress(e.target.checked)}
            className="h-3.5 w-3.5 accent-[#6B0F1A]"
          />
          Same as registered address
        </label>
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">
            Street Address (Line 1)
          </label>
          <Input
            value={form.businessAddressLine1}
            onChange={(v) => updateField('businessAddressLine1', v)}
            disabled={sameAsRegistered}
            placeholder="Operational business address"
          />
        </div>
        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">
            Street Address (Line 2)
          </label>
          <Input
            value={form.businessAddressLine2}
            onChange={(v) => updateField('businessAddressLine2', v)}
            disabled={sameAsRegistered}
            placeholder="Apartment, suite, building"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">City</label>
            <Input
              value={form.businessAddressCity}
              onChange={(v) => updateField('businessAddressCity', v)}
              disabled={sameAsRegistered}
              placeholder="City"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              State / Region
            </label>
            <Input
              value={form.businessAddressState}
              onChange={(v) => updateField('businessAddressState', v)}
              disabled={sameAsRegistered}
              placeholder="State or region"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Zip / Postal Code
            </label>
            <Input
              value={form.businessAddressZip}
              onChange={(v) => updateField('businessAddressZip', v)}
              disabled={sameAsRegistered}
              placeholder="Zip / Postal"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">Country</label>
            <CountrySelect
              value={form.businessAddressCountryCode}
              onChange={(v) => updateField('businessAddressCountryCode', v)}
              disabled={sameAsRegistered}
            />
          </div>
        </div>

        <hr className="border-[#eee] my-4" />

        {/* ── Section 2: Account Usage & Compliance ──────────────────── */}
        <div className="text-[13px] font-bold text-[#1a1a1a] uppercase tracking-[0.5px]">
          2. Account Usage & Compliance
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Business Sector
            </label>
            <Select
              value={form.businessSectorId}
              onChange={(v) => updateField('businessSectorId', v)}
              options={businessSectorOptions}
              placeholder={businessSectorsLoading ? 'Loading…' : 'Select sector…'}
              disabled={businessSectorsLoading}
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">Website</label>
            <UrlInput
              value={form.website}
              onChange={(v) => updateField('website', v)}
              error={errors.website}
              placeholder="https://www.example.com"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">
            Business Activity Description
          </label>
          <textarea
            value={form.activityDescription}
            onChange={(e) => updateField('activityDescription', e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A] resize-none"
            placeholder="Describe the company's main business activities"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Purpose of Account
            </label>
            <Input
              value={form.purposeOfAccount}
              onChange={(v) => updateField('purposeOfAccount', v)}
              placeholder="e.g. Trade Payments"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Intended Use of Account
            </label>
            <Input
              value={form.intendedUse}
              onChange={(v) => updateField('intendedUse', v)}
              placeholder="e.g. Receiving international payments"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">
            Source of Income
          </label>
          <textarea
            value={form.sourceOfIncome}
            onChange={(e) => updateField('sourceOfIncome', e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A] resize-none"
            placeholder="Describe the origin of the company's income"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Monthly Income (EUR)
            </label>
            <input
              type="number"
              value={form.monthlyIncome}
              onChange={(e) => updateField('monthlyIncome', e.target.value)}
              min="0"
              className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
              placeholder="e.g. 25000"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Est. Monthly Incoming (Transactions)
            </label>
            <input
              type="number"
              value={form.estMonthlyIncomingTxns}
              onChange={(e) => updateField('estMonthlyIncomingTxns', e.target.value)}
              min="0"
              className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
              placeholder="e.g. 10"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Est. Monthly Incoming Volume (USD)
            </label>
            <input
              type="number"
              value={form.estMonthlyIncomingVolume}
              onChange={(e) => updateField('estMonthlyIncomingVolume', e.target.value)}
              min="0"
              className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
              placeholder="e.g. 250000"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Est. Monthly Outgoing (Transactions)
            </label>
            <input
              type="number"
              value={form.estMonthlyOutgoingTxns}
              onChange={(e) => updateField('estMonthlyOutgoingTxns', e.target.value)}
              min="0"
              className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
              placeholder="e.g. 8"
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Est. Monthly Outgoing Volume (USD)
            </label>
            <input
              type="number"
              value={form.estMonthlyOutgoingVolume}
              onChange={(e) => updateField('estMonthlyOutgoingVolume', e.target.value)}
              min="0"
              className="w-full rounded-lg border border-[#e0e0e0] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
              placeholder="e.g. 200000"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Countries for Planned Incoming Transactions
            </label>
            <CountryListInput
              value={form.plannedCountriesIn}
              onChange={(v) => updateField('plannedCountriesIn', v)}
            />
          </div>
          <div>
            <label className="mb-1 block text-[12px] font-semibold text-[#666]">
              Countries for Planned Outgoing Transactions
            </label>
            <CountryListInput
              value={form.plannedCountriesOut}
              onChange={(v) => updateField('plannedCountriesOut', v)}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-[#666]">
            Planned Currencies
          </label>
          <CurrencyListInput
            value={form.plannedCurrencies}
            onChange={(v) => updateField('plannedCurrencies', v)}
          />
        </div>
      </div>
    </Modal>
  );
}
